"use client";

import * as React from "react";
import { toast } from "sonner";

/**
 * "Show me the menu the way the restaurant wrote it."
 *
 * WHY THIS IS NOT "en". The code used to treat English as the do-nothing
 * option: picking it cleared the translation and showed the stored menu. That
 * is only correct if every menu is authored in English, and nothing in the
 * schema says so — there is no source-language column anywhere. On a menu
 * written in French (or Twi, or Portuguese) the list still offered "English",
 * but choosing it just cleared the translation and left the guest looking at
 * the original French. English was in the list and unreachable.
 *
 * So "the original" is now its own explicit choice, separate from every real
 * language, and English became a translation target like any other. The
 * fast path is unchanged for the common case: a guest who never touches the
 * control stays on the original and no translation request is made.
 */
// The offered-language list and its helpers are plain data with no React and
// no browser globals, so the server-side AI prompt code can import them too.
// They live in lib/menu/languages.ts; this hook re-exports them so its public
// API is unchanged.
import {
  LANGUAGES,
  ORIGINAL_LANGUAGE,
  isOriginalLanguage,
} from "@/lib/menu/languages";

export { LANGUAGES, ORIGINAL_LANGUAGE, isOriginalLanguage };

const STORAGE_KEY = "mnrq_menu_language";
/**
 * Bumped to v2 when English became a real translation target.
 *
 * While `en` short-circuited on the server it returned the menu UNCHANGED with
 * `ok: true`, and this cache stored that identity response like any other
 * result. Any guest who selected English before the fix now holds a cached
 * "English translation" that is really the untranslated menu, and it stays
 * valid for 24 hours — so the bug would outlive the fix on exactly the devices
 * that hit it. A new prefix retires those entries instead of waiting them out.
 */
const CACHE_PREFIX = "mnrq_menu_translation_v2_";
const CACHE_TTL = 24 * 60 * 60 * 1000;

interface CacheEntry {
  data: Record<string, unknown>;
  timestamp: number;
}

export function useMenuTranslation(slug: string) {
  const [selectedLanguage, setSelectedLanguageState] = React.useState(() => {
    if (typeof window === "undefined") return ORIGINAL_LANGUAGE;
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored && LANGUAGES.some((l) => l.code === stored)) return stored;
    } catch { /* ignore */ }
    return ORIGINAL_LANGUAGE;
  });

  const [isTranslating, setIsTranslating] = React.useState(false);
  const [translatedData, setTranslatedData] = React.useState<Record<string, unknown> | null>(null);
  const abortRef = React.useRef<AbortController | null>(null);

  const setSelectedLanguage = React.useCallback((lang: string) => {
    try { sessionStorage.setItem(STORAGE_KEY, lang); } catch { /* ignore */ }
    setSelectedLanguageState(lang);
  }, []);

  const getCached = React.useCallback((lang: string): Record<string, unknown> | null => {
    try {
      const raw = sessionStorage.getItem(`${CACHE_PREFIX}${slug}_${lang}`);
      if (!raw) return null;
      const entry: CacheEntry = JSON.parse(raw);
      if (Date.now() - entry.timestamp > CACHE_TTL) {
        sessionStorage.removeItem(`${CACHE_PREFIX}${slug}_${lang}`);
        return null;
      }
      return entry.data;
    } catch { return null; }
  }, [slug]);

  const setCache = React.useCallback((lang: string, data: Record<string, unknown>) => {
    try {
      const entry: CacheEntry = { data, timestamp: Date.now() };
      sessionStorage.setItem(`${CACHE_PREFIX}${slug}_${lang}`, JSON.stringify(entry));
    } catch { /* sessionStorage full */ }
  }, [slug]);

  const translate = React.useCallback(async (menuData: Record<string, unknown>, lang: string) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    if (isOriginalLanguage(lang)) {
      setTranslatedData(null);
      return;
    }

    const cached = getCached(lang);
    if (cached) {
      setTranslatedData(cached);
      return;
    }

    setIsTranslating(true);
    try {
      const res = await fetch("/api/ai/translate-menu", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ menuData, targetLanguage: lang }),
        signal: controller.signal,
      });
      if (!res.ok) throw new Error("Translation request failed");
      const data = await res.json();

      // The server tells us when the upstream translator refused most of the
      // strings. Without this the guest picked a language, watched the flag
      // change, and read the same English menu — with nothing anywhere saying
      // why. Do not cache a degraded result, or the failure sticks for the
      // session.
      if (data.degraded) {
        setTranslatedData(null);
        setSelectedLanguage(ORIGINAL_LANGUAGE);
        toast.error("Translation isn't available right now. Showing the original menu.");
        return;
      }

      if (data.ok && data.translated) {
        setCache(lang, data.translated);
        setTranslatedData(data.translated);
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      setTranslatedData(null);
      setSelectedLanguage(ORIGINAL_LANGUAGE);
      toast.error("Couldn't translate the menu just now. Showing the original.");
    } finally {
      setIsTranslating(false);
    }
  }, [getCached, setCache]);

  const clearTranslation = React.useCallback(() => {
    setSelectedLanguage(ORIGINAL_LANGUAGE);
    setTranslatedData(null);
    try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  }, [setSelectedLanguage]);

  return {
    selectedLanguage,
    setSelectedLanguage,
    translatedData,
    isTranslating,
    translate,
    clearTranslation,
  };
}
